import { useMutation, useQuery } from '@apollo/client'
import {
    Delete as DeleteIcon,
    Edit as EditIcon,
    PushPin,
} from '@mui/icons-material'
import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    IconButton,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from '@mui/material'
import { Underline } from '@tiptap/extension-underline'
import { StarterKit } from '@tiptap/starter-kit'
import {
    CandidateGroupNotes,
    CandidateNote,
    Companies,
    GetCandidatesByIdsQuery,
    NoteTemplate,
    TemplateType,
} from '__generated__/graphql'
import isEqual from 'lodash.isequal'
import startCase from 'lodash.startcase'
import {
    MenuButtonBold,
    MenuButtonBulletedList,
    MenuButtonItalic,
    MenuButtonOrderedList,
    MenuButtonUnderline,
    MenuControlsContainer,
    MenuDivider,
    MenuSelectHeading,
    RichTextEditor,
    type RichTextEditorRef,
} from 'mui-tiptap'
import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import { Link, useBlocker, useParams } from 'react-router-dom'
import {
    AddCandidateGroupNote,
    DeleteCandidateGroupNote,
    UpdateCandidateGroupNote,
} from 'recruiter/graphql/candidateGroupNotes.gql'
import type { CandidateNote as CandidateNoteType } from 'recruiter/graphql/candidateNotes.gql'
import {
    AddCandidateNote,
    DeleteCandidateNote,
    GetCandidateNoteTemplates,
    UpdateCandidateNote,
} from 'recruiter/graphql/candidateNotes.gql'
import { GetNoteContent } from 'recruiter/graphql/candidateProfile.gql'
import { recruiterRouteConfig } from 'recruiter/routes'
import theme from 'recruiter/theme'
import CandidatesAutocompleteMultiple from 'shared/components/CandidatesAutocomplete/CandidatesAutocompleteMultiple'
import CompaniesAutocomplete from 'shared/components/CompaniesAutocomplete/CompaniesAutocomplete'
import DrawerComponent from 'shared/components/DrawerComponent'
import RostersAutocomplete from 'shared/components/RostersAutocomplete/RostersAutocomplete'
import StarRatings from 'shared/components/StarRatings'
import useToast from 'shared/components/Toast/useToast'
import UnsavedChangesModal from 'shared/components/UnsavedChangesModal/UnsavedChangesModal'
import { getFullName, getPxAsRem } from 'shared/utils'
import { useNotesContext } from 'shared/utils/noteContext'
import { stripHtmlTags } from 'shared/utils/stripTags'

interface EditProfileProps {
    parentPath: string
    open: boolean
    onClose: () => void
    note: CandidateNoteType | null
    refetch: () => void
    currentCandidate: GetCandidatesByIdsQuery['candidatesById'][number]
}

const EditNotes: FC<EditProfileProps> = ({
    open,
    onClose,
    note,
    refetch,
    parentPath,
    currentCandidate,
}) => {
    const rteRef = useRef<RichTextEditorRef>(null)
    const toast = useToast()
    const { candidateId } = useParams()
    const { data: noteTemplates } = useQuery(GetCandidateNoteTemplates)

    // single note mutations
    const [addCandidateNote] = useMutation(AddCandidateNote)
    const [updateCandidateNote] = useMutation(UpdateCandidateNote)
    const [deleteCandidateNote] = useMutation(DeleteCandidateNote)

    // group note mutations
    const [addCandidateGroupNote] = useMutation(AddCandidateGroupNote)
    const [updateCandidateGroupNote] = useMutation(UpdateCandidateGroupNote)
    const [deleteCandidateGroupNote] = useMutation(DeleteCandidateGroupNote)

    const [selectedTemplate, setSelectedTemplate] = useState<TemplateType>(
        note?.template?.templateType ?? TemplateType.Blank
    )

    const { data: noteData } = useQuery(GetNoteContent, {
        skip: !note,
        variables: { noteId: note?.noteId ?? '' },
        onCompleted: (data) => {
            setNoteContent(
                data?.getNoteContent?.content ?? 'Error fetching note'
            )
        },
    })

    // Initialize candidates: for existing group notes use the note's candidates,
    // for new notes pre-select the current candidate
    const getInitialCandidates =
        (): GetCandidatesByIdsQuery['candidatesById'] => {
            if (note?.noteType === 'group') {
                return (
                    (note?.note as CandidateGroupNotes)?.groupNote
                        ?.candidates || []
                )
            }
            // For new notes, pre-select the current candidate
            return [currentCandidate]
        }
    const [candidates, setCandidates] = useState<
        GetCandidatesByIdsQuery['candidatesById']
    >(getInitialCandidates())
    // Initialize company/roster from either single note or group note
    const getInitialCompany = (): Companies | null => {
        if (note?.noteType === 'group') {
            return (
                (note?.note as CandidateGroupNotes)?.groupNote?.company || null
            )
        }
        return (note?.note as CandidateNote)?.company || null
    }
    const getInitialRoster = (): { id: string | null; name: string | null } => {
        if (note?.noteType === 'group') {
            const groupNote = note?.note as CandidateGroupNotes
            return {
                id: groupNote?.groupNote?.roster?.id || null,
                name: groupNote?.groupNote?.roster?.rosterName || null,
            }
        }
        const singleNote = note?.note as CandidateNote
        return {
            id: singleNote?.rosterId || null,
            name: singleNote?.roster?.rosterName || null,
        }
    }
    const [company, setCompany] = useState<Companies | null>(
        getInitialCompany()
    )
    const [rosterName, setRosterName] = useState<string | null>(
        getInitialRoster().name
    )
    const [rosterId, setRosterId] = useState<string | null>(
        getInitialRoster().id
    )
    const [noteType, setNoteType] = useState<string>(note?.noteType ?? 'single')
    const [noteStarRating, setNoteStarRating] = useState<number | null>(
        note?.starRating || null
    )
    const [hover, setHover] = React.useState(-1)
    const [excludeFromEvaluation, setExcludeFromEvaluation] = useState<boolean>(
        note?.excludeFromEvaluation || false
    )

    const [isEditingCandidates, setIsEditingCandidates] = useState(false)
    const [isEditingCompany, setIsEditingCompany] = useState(false)
    const [isEditingRoster, setIsEditingRoster] = useState(false)
    const { closeEditNote } = useNotesContext()

    const setNoteContent = (noteContent) =>
        rteRef.current?.editor?.commands.setContent(noteContent)

    const labels: { [index: number]: string } = {
        0.5: 'Red Flag',
        1: 'B-',
        1.5: 'B/B-',
        2: 'B',
        2.5: 'B/B+',
        3: 'B+',
        3.5: 'A-/B+',
        4: 'A-',
        4.5: 'A/A-',
        5: 'A',
    }
    const getSelectedNoteTemplate = useCallback(
        (): Partial<NoteTemplate> =>
            noteTemplates?.noteTemplates.find(
                (t) => t.templateType === selectedTemplate
            ) || {},
        [noteTemplates?.noteTemplates, selectedTemplate]
    )
    const canDeleteTemplates =
        noteTemplates?.noteTemplates
            .filter((i) => i.canDelete)
            .map((i) => i.templateType) || []
    const canDelete = canDeleteTemplates.includes(selectedTemplate)
    const isEditingNote = Boolean(note)
    const selectedTemplateData = noteTemplates?.noteTemplates.find(
        (i) => i.templateType === selectedTemplate
    )
    const hasStarRating = selectedTemplateData?.hasStarRating ?? false
    const starRatingRequired = selectedTemplateData?.starRatingRequired ?? false
    // Only show star ratings for single notes, not group notes
    const showStarRating = hasStarRating && noteType !== 'group'
    const bannedNoteTemplates = ['Main', 'Historical', 'Historical Grade']
    const filteredTemplates =
        noteTemplates?.noteTemplates
            .filter((i) => !bannedNoteTemplates.includes(i.templateType))
            .map((i) => i.templateType) || []

    const selectIsDisabled =
        (!filteredTemplates.includes(selectedTemplate) &&
            getSelectedNoteTemplate()?.canEdit === false) ||
        bannedNoteTemplates.includes(selectedTemplate)
    const noteTemplatesForSelection =
        isEditingNote && bannedNoteTemplates.includes(selectedTemplate)
            ? [getSelectedNoteTemplate()]
            : noteTemplates?.noteTemplates.filter((i) => {
                  if (
                      isEditingNote &&
                      !bannedNoteTemplates.includes(i.templateType)
                  )
                      return true

                  if (!isEditingNote) return i.canCreate

                  if (isEditingNote) {
                      return filteredTemplates.includes(i.templateType)
                  } else {
                      return i.canCreate
                  }
              }) || []

    // close notes edit drawer when navigating away
    const blocker = useBlocker(({ currentLocation, nextLocation }) => {
        if (!open) return false
        const unsavedChangesExist = checkForUnsavedChanges()
        const isNavigatingAwayFromRoot =
            !nextLocation.pathname.includes(parentPath)
        const isNavigatingInternally =
            !isNavigatingAwayFromRoot &&
            currentLocation.pathname !== nextLocation.pathname
        const showWarningModal = isNavigatingAwayFromRoot && unsavedChangesExist
        const updatedSearch = nextLocation.search !== currentLocation.search
        if (!showWarningModal && !isNavigatingInternally && !updatedSearch) {
            closeNote(false)
        }
        setShowUnsavedChangesModal(showWarningModal)
        // return false to not block navigation
        return showWarningModal
    })

    const setShowUnsavedChangesModal = (value: boolean) => {
        _setShowUnsavedChangesModal(value)
    }

    const [showUnsavedChangesModal, _setShowUnsavedChangesModal] =
        useState(false)

    const resetNoteFields = useCallback(() => {
        setNoteType('single')
        setNoteContent('')
        setSelectedTemplate(TemplateType.Blank)
        setNoteStarRating(null)
        setHover(-1)
        setExcludeFromEvaluation(false)

        setCandidates([currentCandidate])
        setCompany(null)
        setRosterId(null)
        setIsEditingCandidates(false)
        setIsEditingCompany(false)
        setIsEditingRoster(false)
        setShowUnsavedChangesModal(false)
    }, [currentCandidate])

    useEffect(() => {
        const foundSelectedTemplate = getSelectedNoteTemplate()

        if (
            foundSelectedTemplate &&
            foundSelectedTemplate?.canEdit !== undefined
        ) {
            rteRef.current?.editor?.setEditable(foundSelectedTemplate?.canEdit)
        }
    }, [
        noteTemplates?.noteTemplates,
        selectedTemplate,
        getSelectedNoteTemplate,
    ])

    useEffect(() => {
        if (note && noteData) {
            setNoteType(note.noteType || 'single')
            setSelectedTemplate(
                note.template?.templateType ?? TemplateType.Blank
            )
            setNoteContent(noteData ?? 'Loading...')
            setNoteStarRating(note.starRating || null)
            setHover(note.starRating || -1)

            if (note.noteType === 'group') {
                const groupNote = note.note as CandidateGroupNotes
                setCandidates(groupNote?.groupNote?.candidates || [])
                setCompany(groupNote?.groupNote?.company || null)
                setRosterId(groupNote?.groupNote?.roster?.id || null)
                setRosterName(groupNote?.groupNote?.roster?.rosterName || null)
            } else {
                // Single note - set company/roster from the single note data
                const singleNote = note.note as CandidateNote
                setCompany(singleNote?.company || null)
                setRosterId(singleNote?.rosterId || null)
                setRosterName(singleNote?.roster?.rosterName || null)
            }
        } else {
            setSelectedTemplate(TemplateType.Blank)
            resetNoteFields()
        }
    }, [note, noteData, resetNoteFields])

    useEffect(() => {
        if (open && !note) {
            // For new notes, enable editing mode for company/roster
            setIsEditingCompany(true)
            setIsEditingRoster(true)
            if (noteType === 'group') {
                setIsEditingCandidates(true)
            }
        }
    }, [open, note, noteType])

    const checkForUnsavedChanges = () => {
        if (!note) {
            if (!rteRef.current?.editor?.isEmpty) return true
            // Check if company or roster was selected for a new note
            if (company?.id || rosterId) return true
            // Check if candidates were modified for a new group note
            // (default is just the current candidate pre-selected)
            if (
                noteType === 'group' &&
                !isEqual(
                    candidates.map((c) => c.id),
                    [currentCandidate.id]
                )
            )
                return true
            return false
        }
        const noteContent = rteRef.current?.editor?.getHTML()
        if (noteType !== note.noteType) return true
        if (selectedTemplate !== note.template?.templateType) return true
        if (noteContent !== noteData) return true
        if (![note.starRating, null].includes(noteStarRating)) return true

        if (note.noteType === 'group') {
            const groupNote = note.note as CandidateGroupNotes
            if (
                !isEqual(
                    candidates.map((c) => c.id),
                    groupNote?.groupNote?.candidates.map((c) => c.id)
                )
            )
                return true
            if (company?.id !== groupNote?.groupNote?.company?.id) return true
            if (rosterId !== groupNote?.groupNote?.roster?.id) return true
        } else {
            // Single note - check company/roster changes
            const singleNote = note.note as CandidateNote
            if (company?.id !== singleNote?.company?.id) return true
            if (rosterId !== singleNote?.rosterId) return true
        }
        return false
    }

    const handleMutationError = (error) => {
        console.error(error)
        toast({
            title: 'Error',
            message: 'Something went wrong. Please try again later.',
            severity: 'error',
        })
    }

    const handleSaveNote = async () => {
        const selectedTemplateObject = noteTemplates?.noteTemplates.find(
            (t) => t.templateType === selectedTemplate
        )
        const templateId = selectedTemplateObject?.id
        if (!templateId || !stripHtmlTags(rteRef.current?.editor?.getHTML())) {
            toast({
                title: 'Error',
                message: 'Please select a template and enter the note content.',
                severity: 'error',
            })
            return
        }

        if (starRatingRequired && !excludeFromEvaluation && !noteStarRating) {
            toast({
                title: 'Error',
                message: 'Please select a star rating for this note type.',
                severity: 'error',
            })
            return
        }

        // If group note, ensure we have at least one candidate
        if (noteType === 'group' && candidates.length === 0) {
            toast({
                title: 'Error',
                message: 'Please select at least one candidate.',
                severity: 'error',
            })
            return
        }

        const singleNoteVariables = {
            candidateId: candidateId || '',
            note: rteRef.current?.editor?.getHTML(),
            starRating: noteStarRating
                ? parseFloat(noteStarRating.toString())
                : null,
            templateId,
            excludeFromEvaluation,
            companyId: company?.id,
            rosterId: rosterId,
        }

        // Use the selected candidates directly
        const selectedCandidateIds =
            candidates?.map((candidate) => candidate.id) || []

        const groupNoteVariables = {
            candidateIds: selectedCandidateIds,
            companyId: company?.id,
            rosterId: rosterId,
            templateId,
            noteData: rteRef.current?.editor?.getHTML() || '',
            isPinned: note?.isPinned || false,
            starRating: 0, // Group notes don't have star ratings
            excludeFromEvaluation: false, // Group notes don't use excludeFromEvaluation
        }

        if (note) {
            if (noteType === 'group') {
                updateCandidateGroupNote({
                    variables: {
                        ...groupNoteVariables,
                        noteId: note.noteId || '',
                    },
                    onCompleted: (data) => {
                        if (data.updateCandidateGroupNote.success) {
                            refetch()
                            closeNote(true)
                            toast({
                                title: 'Success',
                                message: 'Group note updated successfully.',
                                severity: 'success',
                            })
                        } else {
                            toast({
                                title: 'Error',
                                message:
                                    data.updateCandidateGroupNote.message ??
                                    'Failed to update group note.',
                                severity: 'error',
                            })
                        }
                    },
                    onError: handleMutationError,
                })
            } else {
                updateCandidateNote({
                    variables: {
                        ...singleNoteVariables,
                        noteId: note.noteId || '',
                    },
                    onCompleted: (data) => {
                        if (data.updateCandidateNote.success) {
                            refetch()
                            closeNote(true)
                            toast({
                                title: 'Success',
                                message: 'Note updated successfully.',
                                severity: 'success',
                            })
                        } else {
                            toast({
                                title: 'Error',
                                message:
                                    data.updateCandidateNote.message ??
                                    'Failed to update note.',
                                severity: 'error',
                            })
                        }
                    },
                    onError: handleMutationError,
                })
            }
        } else {
            if (noteType === 'group') {
                addCandidateGroupNote({
                    variables: groupNoteVariables,
                    onCompleted: (data) => {
                        if (data.addCandidateGroupNote.success) {
                            refetch()
                            closeNote(true)
                            toast({
                                title: 'Success',
                                message: 'Group note added successfully.',
                                severity: 'success',
                            })
                        } else {
                            toast({
                                title: 'Error',
                                message:
                                    data.addCandidateGroupNote.message ??
                                    'Failed to add group note.',
                                severity: 'error',
                            })
                        }
                    },
                    onError: handleMutationError,
                })
            } else {
                addCandidateNote({
                    variables: singleNoteVariables,
                    onCompleted: (data) => {
                        if (data.addCandidateNote.success) {
                            refetch()
                            closeNote(true)
                            toast({
                                title: 'Success',
                                message: 'Note added successfully.',
                                severity: 'success',
                            })
                        } else {
                            toast({
                                title: 'Error',
                                message:
                                    data.addCandidateNote.message ??
                                    'Failed to add note.',
                                severity: 'error',
                            })
                        }
                    },
                    onError: handleMutationError,
                })
            }
        }
    }

    const NoteStarRatings = () => (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                alignItems: 'start',
                justifyContent: 'space-between',
                marginBottom: isEditingNote ? '1rem' : undefined,
                marginLeft: isEditingNote ? '0.3rem' : undefined,
            }}
        >
            <FormControl>
                <StarRatings
                    starRating={noteStarRating}
                    setStarRating={setNoteStarRating}
                    hover={hover}
                    setHover={setHover}
                    customLabels={labels}
                    canEdit={getSelectedNoteTemplate()?.canEdit}
                />
                {starRatingRequired &&
                    !excludeFromEvaluation &&
                    !noteStarRating && (
                        <Typography variant="caption" color="error">
                            Rating required
                        </Typography>
                    )}
            </FormControl>
            {hasStarRating && (
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={excludeFromEvaluation}
                            onChange={(e) =>
                                setExcludeFromEvaluation(e.target.checked)
                            }
                        />
                    }
                    label="Remeet"
                />
            )}
        </Box>
    )

    const handleDeleteNote = async () => {
        if (!note) return
        if (noteType === 'group') {
            await deleteCandidateGroupNote({
                variables: { noteId: note.noteId || '' },
                onCompleted: (data) => {
                    if (data.deleteCandidateGroupNote.success) {
                        refetch()
                        closeNote(true)
                        toast({
                            title: 'Success',
                            message: 'Group note deleted successfully.',
                            severity: 'success',
                        })
                    } else {
                        toast({
                            title: 'Error',
                            message:
                                data.deleteCandidateGroupNote.message ??
                                'Failed to delete group note.',
                            severity: 'error',
                        })
                    }
                },
                onError: handleMutationError,
            })
        } else {
            await deleteCandidateNote({
                variables: { noteId: note.noteId || '' },
                onCompleted: (data) => {
                    if (data.deleteCandidateNote.success) {
                        refetch()
                        closeNote(true)
                        toast({
                            title: 'Success',
                            message: 'Note deleted successfully.',
                            severity: 'success',
                        })
                    } else {
                        toast({
                            title: 'Error',
                            message:
                                data.deleteCandidateNote.message ??
                                'Failed to delete note.',
                            severity: 'error',
                        })
                    }
                },
                onError: handleMutationError,
            })
        }
    }

    const noteFormTitle = () => {
        if (note) {
            return noteType === 'group' ? 'Edit Group Note' : 'Edit Note'
        } else {
            return noteType === 'group' ? 'Add Group Note' : 'Add Note'
        }
    }

    //Prevent page refresh is there are unsaved changes
    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (open && checkForUnsavedChanges()) {
                e.preventDefault()
            }
        }
        window.addEventListener('beforeunload', handleBeforeUnload)
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload)
        }
    })

    const closeNote = (forceClose: boolean) => {
        if (checkForUnsavedChanges() && !forceClose) {
            setShowUnsavedChangesModal(true)
            return
        }
        resetNoteFields()
        closeEditNote()
        onClose()
    }

    return (
        <DrawerComponent
            open={open}
            variant="persistent"
            anchor="right"
            onClose={() => closeNote(false)}
            hasCancelButton
            onCancel={() => closeNote(true)}
            primaryActionText={note ? 'Update' : 'Add'}
            onPrimaryAction={handleSaveNote}
            headerTitle={noteFormTitle()}
        >
            <UnsavedChangesModal
                open={showUnsavedChangesModal || blocker.state === 'blocked'}
                onLeaveClick={() => {
                    closeNote(true)
                    if (blocker.proceed) {
                        blocker.proceed()
                    }
                }}
                onStayClick={() => {
                    setShowUnsavedChangesModal(false)
                    if (blocker.reset) {
                        blocker.reset()
                    }
                }}
            />
            <Box>
                {!note && (
                    <RadioGroup
                        defaultValue={noteType}
                        value={noteType}
                        onChange={(e) => setNoteType(e.target.value)}
                        row
                        sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-evenly',
                            flexGrow: 1,
                            margin: 3,
                            marginTop: 1,
                        }}
                    >
                        <FormControlLabel
                            control={<Radio value={'single'} />}
                            label="Single"
                        />
                        <FormControlLabel
                            control={<Radio value={'group'} />}
                            label="Group"
                        />
                    </RadioGroup>
                )}
                {isEditingNote &&
                    note?.template &&
                    note.template.hasStarRating &&
                    noteType !== 'group' && <NoteStarRatings />}
                <Autocomplete
                    disabled={selectIsDisabled || isEditingNote}
                    options={noteTemplatesForSelection}
                    getOptionLabel={(option: Partial<NoteTemplate>) =>
                        startCase(option.templateType) ?? ''
                    }
                    value={
                        noteTemplatesForSelection.find(
                            (template) =>
                                template.templateType === selectedTemplate
                        ) || null
                    }
                    onChange={(_event, newValue) => {
                        const selectedType =
                            newValue?.templateType ?? TemplateType.Blank
                        setSelectedTemplate(selectedType as TemplateType)
                        setNoteContent('')

                        // Update star rating requirement based on the new template selection
                        if (
                            noteTemplates?.noteTemplates.some(
                                (template) =>
                                    template.templateType === selectedType &&
                                    template.hasStarRating
                            )
                        ) {
                            setNoteStarRating(note?.starRating || null) // Preserve star rating if available
                        } else {
                            setNoteStarRating(null) // Reset star rating if not needed
                        }
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            fullWidth
                            label="Template"
                            sx={{
                                marginY: getPxAsRem(16),
                            }}
                        />
                    )}
                />
                {note?.isPinned && (
                    <Box
                        display="flex"
                        alignItems="center"
                        sx={{ marginBottom: getPxAsRem(16) }}
                    >
                        <PushPin sx={{ color: '#1976d2' }} />
                        <Typography
                            variant="h6"
                            sx={{ marginLeft: getPxAsRem(8) }}
                        >
                            Pinned Note
                        </Typography>
                    </Box>
                )}
                <div>
                    <Box sx={{ '& .ProseMirror': { minHeight: 200 } }}>
                        <RichTextEditor
                            ref={rteRef}
                            extensions={[StarterKit, Underline]}
                            renderControls={() => (
                                <MenuControlsContainer>
                                    <MenuSelectHeading />
                                    <MenuDivider />
                                    <MenuButtonBold />
                                    <MenuButtonItalic />
                                    <MenuButtonUnderline />
                                    <MenuButtonBulletedList />
                                    <MenuButtonOrderedList />
                                </MenuControlsContainer>
                            )}
                        />
                    </Box>
                </div>
                {!isEditingNote && showStarRating && <NoteStarRatings />}

                {noteType === 'group' && (
                    <>
                        <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ marginTop: getPxAsRem(16) }}
                        >
                            <Typography
                                variant="body1"
                                color={theme.palette.grey[600]}
                                mr={2}
                            >
                                Candidates:
                            </Typography>
                            {isEditingNote && (
                                <IconButton
                                    onClick={() =>
                                        setIsEditingCandidates(
                                            !isEditingCandidates
                                        )
                                    }
                                    color={
                                        isEditingCandidates
                                            ? 'primary'
                                            : 'default'
                                    }
                                    sx={{
                                        backgroundColor: isEditingCandidates
                                            ? theme.palette.primary.light + '20'
                                            : 'transparent',
                                    }}
                                >
                                    <EditIcon />
                                </IconButton>
                            )}
                        </Box>
                        {isEditingCandidates || !isEditingNote ? (
                            <Box display="flex">
                                <CandidatesAutocompleteMultiple
                                    ChipProps={{ size: 'small' }}
                                    fullWidth
                                    value={candidates || []}
                                    isOptionEqualToValue={(option, value) =>
                                        option.id === value.id
                                    }
                                    onChange={(_event, value) =>
                                        setCandidates(value)
                                    }
                                    getOptionLabel={(option) =>
                                        getFullName(option.user || {})
                                    }
                                    showSecondaryText
                                />
                            </Box>
                        ) : (
                            <Box display="flex" flexWrap="wrap">
                                {candidates.map((candidate, index) => (
                                    <Typography
                                        variant="body2"
                                        key={index}
                                        sx={{ paddingRight: '0.25rem' }}
                                    >
                                        <Link
                                            onClick={(e) => e.stopPropagation()}
                                            to={`${recruiterRouteConfig.candidates.path}/${candidate.id}`}
                                            style={{
                                                color: theme.palette.primary
                                                    .main,
                                                textDecoration: 'none',
                                            }}
                                        >
                                            {candidate.user?.nickname ??
                                                candidate.user?.firstName}{' '}
                                            {candidate.user?.lastName}
                                        </Link>
                                        {index < candidates.length - 1
                                            ? ', '
                                            : ''}
                                    </Typography>
                                ))}
                            </Box>
                        )}
                    </>
                )}

                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ marginTop: getPxAsRem(16) }}
                >
                    <Typography
                        variant="body1"
                        color={theme.palette.grey[600]}
                        mr={2}
                    >
                        Company:
                    </Typography>
                    {isEditingNote && (
                        <IconButton
                            onClick={() =>
                                setIsEditingCompany(!isEditingCompany)
                            }
                            color={isEditingCompany ? 'primary' : 'default'}
                            sx={{
                                backgroundColor: isEditingCompany
                                    ? theme.palette.primary.light + '20'
                                    : 'transparent',
                            }}
                        >
                            <EditIcon />
                        </IconButton>
                    )}
                </Box>
                {isEditingCompany || !isEditingNote ? (
                    <Box display="flex">
                        <CompaniesAutocomplete
                            ChipProps={{ size: 'small' }}
                            fullWidth
                            value={company}
                            onChange={(_event, value) =>
                                setCompany(value as Companies)
                            }
                            getOptionLabel={(option) => option.value}
                            placeholder="Associate with a company..."
                        />
                    </Box>
                ) : (
                    <Box display="flex" flexWrap="wrap">
                        <Typography
                            variant="body2"
                            noWrap
                            key={company?.id}
                            sx={{ paddingRight: '0.25rem' }}
                        >
                            {company?.id ? (
                                <Link
                                    onClick={(e) => e.stopPropagation()}
                                    to={`${recruiterRouteConfig.companies.path}/${company?.id}`}
                                    style={{
                                        color: theme.palette.primary.main,
                                        textDecoration: 'none',
                                    }}
                                >
                                    {company?.value}
                                </Link>
                            ) : (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    No company selected
                                </Typography>
                            )}
                        </Typography>
                    </Box>
                )}

                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ marginTop: getPxAsRem(16) }}
                >
                    <Typography
                        variant="body1"
                        color={theme.palette.grey[600]}
                        mr={2}
                    >
                        Roster:
                    </Typography>
                    {isEditingNote && (
                        <IconButton
                            onClick={() => setIsEditingRoster(!isEditingRoster)}
                            color={isEditingRoster ? 'primary' : 'default'}
                            sx={{
                                backgroundColor: isEditingRoster
                                    ? theme.palette.primary.light + '20'
                                    : 'transparent',
                            }}
                        >
                            <EditIcon />
                        </IconButton>
                    )}
                </Box>
                {isEditingRoster || !isEditingNote ? (
                    <Box display="flex">
                        <RostersAutocomplete
                            ChipProps={{ size: 'small' }}
                            fullWidth
                            initialValue={
                                rosterId && rosterName
                                    ? ({
                                          id: rosterId,
                                          rosterName: rosterName,
                                      } as Parameters<
                                          typeof RostersAutocomplete
                                      >[0]['initialValue'])
                                    : null
                            }
                            onSingleSelectionChange={(value) => {
                                setRosterId(value?.id ?? null)
                                setRosterName(value?.rosterName ?? null)
                            }}
                            placeholder="Associate with a search..."
                        />
                    </Box>
                ) : (
                    <Box display="flex" flexWrap="wrap">
                        <Typography
                            variant="body2"
                            noWrap
                            key={rosterId}
                            sx={{ paddingRight: '0.25rem' }}
                        >
                            {rosterId ? (
                                <Link
                                    onClick={(e) => e.stopPropagation()}
                                    to={`${recruiterRouteConfig.rosters.path}/${rosterId}`}
                                    style={{
                                        color: theme.palette.primary.main,
                                        textDecoration: 'none',
                                    }}
                                >
                                    {rosterName}
                                </Link>
                            ) : (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    No roster selected
                                </Typography>
                            )}
                        </Typography>
                    </Box>
                )}
                <Box
                    display="flex"
                    justifyContent="flex-start"
                    alignItems="center"
                    sx={{
                        marginBottom: getPxAsRem(16),
                        marginTop: getPxAsRem(36),
                    }}
                >
                    {isEditingNote && canDelete && (
                        <Button
                            onClick={handleDeleteNote}
                            startIcon={<DeleteIcon />}
                            variant="text"
                            color="error"
                        >
                            Delete Note
                        </Button>
                    )}
                </Box>
            </Box>
        </DrawerComponent>
    )
}

export default EditNotes
